import { useMutation } from "@apollo/client";
import { Button, Card, FormGroup, InputGroup } from "@blueprintjs/core";
import gql from "graphql-tag";
import React, { useState } from "react";
import { BackendError, parseError } from "../../components/Error";
import { FlexContainer } from "../../components/flex/Flex";
import { ErrorCard } from "../../components/form/Error";
import { UserLanguage } from "../../lang/lang";
import { _t } from "../../utils/pack";

enum LoginProcessState {
  Idle,
  LoggingIn,
  Success,
  Failed,
}

interface LoginState {
  identifier: string;
  password: string;
  state: LoginProcessState;
  error?: BackendError | null;
}

interface AuthenticateMutationVariables {
  id: string;
  password: string;
}

const AuthenticateMutation = gql`
  mutation authenticate($id: String!, $password: String!) {
    authenticate(identifier: $id, password: $password)
  }
`;

export const LoginView = () => {
  const [loginState, setLoginState] = useState<LoginState>({
    password: "",
    identifier: "",
    state: LoginProcessState.Idle,
  });

  const [authenticate] = useMutation<
    { authenticate: boolean },
    AuthenticateMutationVariables
  >(AuthenticateMutation);
  const disableControls = (state: LoginProcessState) =>
    state === LoginProcessState.LoggingIn ||
    state === LoginProcessState.Success;

  const buttonIntent =
    loginState.state === LoginProcessState.Failed
      ? "danger"
      : loginState.state === LoginProcessState.Success
      ? "success"
      : "primary";

  return (
    <FlexContainer>
      <Card elevation={3}>
        <h2>{UserLanguage().pages.login.title}</h2>
        <ErrorCard error={loginState.error} />
        <form onSubmit={(e) => e.preventDefault()}>
          <FormGroup inline={true}>
            <InputGroup
              id="identifier"
              type="text"
              placeholder={UserLanguage().pages.login.userId}
              leftIcon="user"
              disabled={disableControls(loginState.state)}
              onChange={(event: React.FormEvent<HTMLInputElement>) =>
                setLoginState({
                  ...loginState,
                  state: LoginProcessState.Idle,
                  identifier: event.currentTarget.value,
                })
              }
            />
          </FormGroup>
          <FormGroup inline={true}>
            <InputGroup
              id="password"
              type="password"
              placeholder={UserLanguage().pages.login.password}
              leftIcon="key"
              disabled={disableControls(loginState.state)}
              onChange={(event: React.FormEvent<HTMLInputElement>) =>
                setLoginState({
                  ...loginState,
                  state: LoginProcessState.Idle,
                  password: event.currentTarget.value,
                })
              }
            />
          </FormGroup>
          <Button
            type="submit"
            intent={buttonIntent}
            disabled={disableControls(loginState.state)}
            {...(loginState.state === LoginProcessState.Success
              ? { rightIcon: "confirm" }
              : {})}
            {...(loginState.state === LoginProcessState.Failed
              ? { rightIcon: "cross" }
              : {})}
            {...(loginState.state === LoginProcessState.LoggingIn
              ? { rightIcon: "time" }
              : {})}
            onClick={() =>
              _t(
                setLoginState({
                  ...loginState,
                  state: LoginProcessState.LoggingIn,
                  error: null,
                })
              )
                .then(() =>
                  authenticate({
                    variables: {
                      id: loginState.identifier,
                      password: loginState.password,
                    },
                  })
                )
                .then((result) => {
                  if (result.data?.authenticate) {
                    setTimeout(() => window.location.reload(), 250);
                    setLoginState({
                      ...loginState,
                      state: LoginProcessState.Success,
                    });
                  }
                })
                .catch((e) => {
                  console.log(e);
                  const t = parseError(e);
                  console.log(t);

                  setLoginState({
                    ...loginState,
                    state: LoginProcessState.Failed,
                    error: t as BackendError,
                  });
                })
            }
          >
            {UserLanguage().pages.login.title}
          </Button>
        </form>
      </Card>
    </FlexContainer>
  );
};
